// src/store/productManagment/actions.ts v7

import { Product } from '../../dataTypes/productManagment/types';

import {
  FETCH_PRODUCT_REQUEST,
  FETCH_PRODUCT_SUCCESS,
  FETCH_PRODUCT_FAILURE,
  FETCH_PRODUCT_BY_ID_REQUEST,
  FETCH_PRODUCT_BY_ID_SUCCESS,
  FETCH_PRODUCT_BY_ID_FAILURE,
  FETCH_PRODUCT_BY_CATEGORY_REQUEST,
  FETCH_PRODUCT_BY_CATEGORY_SUCCESS,
  FETCH_PRODUCT_BY_CATEGORY_FAILURE,
  ProductActionTypes,
  GetProductListParams
} from './types';

import { Dispatch } from 'redux';
import { RootState } from '../generalReducer';
import { store } from '../store';

import { getProducts } from '../../backendServices/productManagment/api_product';

export const fetchProductList = (params: GetProductListParams = {}, filter: string = '', renew: boolean = false, adminId?: number) => async (dispatch: Dispatch<ProductActionTypes>, getState: () => RootState) => {

  const state = getState();
  const lastFetched = state.productListReducer.lastFetchTimestamp;
  const lastFetchParams = state.productListReducer.lastFetchParams;
  const cacheDuration = 10 * 60 * 1000; // 10 minutes en millisecondes

  const isParamsIncluded = (lastFetchParams?.kind && params.kind)
    ? (params.kind === lastFetchParams.kind)
    : (lastFetchParams?.categoryId !== undefined && params.categoryId !== undefined)
    ? (params.categoryId === lastFetchParams.categoryId)
    : (lastFetchParams?.active !== undefined && params.active !== undefined)
    ? (params.active === lastFetchParams.active)
    : true;

  // Le cache n'est pas indexé par adminId : on le contourne dès qu'un filtre adminId est demandé
  if (lastFetched && Date.now() - lastFetched < cacheDuration && isParamsIncluded && !renew && !adminId) {
    return;
  }

  dispatch({ type: FETCH_PRODUCT_REQUEST, payload: params });

  try {
    const response = await getProducts(filter, adminId);

    if (response.code === 1) {
      // Filtrer les produits selon les paramètres
      let filteredProducts = response.payload;
      
      if (params.kind) {
        filteredProducts = filteredProducts.filter(p => p.kind === params.kind);
      }
      
      if (params.active !== undefined) {
        filteredProducts = filteredProducts.filter(p => p.active === params.active);
      }
      
      if (params.categoryId !== undefined) {
        const categoryId = params.categoryId;
        filteredProducts = filteredProducts.filter(p => p.categoriesIds.includes(categoryId));
      }

      dispatch({
        type: FETCH_PRODUCT_SUCCESS,
        payload: {
          products: filteredProducts,
          lastFetchTimestamp: Date.now(),
          lastFetchParams: params
        }
      });
    } else {
      dispatch({ type: FETCH_PRODUCT_FAILURE, payload: response.comment });
    }
  } catch (error: unknown) {
    dispatch({ type: FETCH_PRODUCT_FAILURE, payload: (error as Error).message || 'Une erreur inconnue est survenue.' });
  }
};

export const fetchProductById = (productId: number) => async (dispatch: Dispatch<ProductActionTypes>, getState: () => RootState) => {
  const state = getState();
  const products = state.productListReducer.products;

  dispatch({ type: FETCH_PRODUCT_BY_ID_REQUEST });

  if (products.has(productId)) {
    return;
  }

  try {
    // Récupérer tous les produits et trouver celui qui correspond
    const response = await getProducts();

    if (response.code === 1) {
      const product = response.payload.find(p => p.id === productId);
      
      if (product) {
        dispatch({ type: FETCH_PRODUCT_BY_ID_SUCCESS, payload: product });
      } else {
        dispatch({ type: FETCH_PRODUCT_BY_ID_FAILURE, payload: 'Produit non trouvé' });
      }
    } else {
      dispatch({ type: FETCH_PRODUCT_BY_ID_FAILURE, payload: response.comment });
    }
  } catch (error: unknown) {
    dispatch({ type: FETCH_PRODUCT_BY_ID_FAILURE, payload: (error as Error).message || 'Une erreur inconnue est survenue.' });
  }
};

export const fetchProductListByCategory = (categoryId: number) => async (dispatch: Dispatch<ProductActionTypes>, getState: () => RootState) => {
  const state = getState();
  const products = state.productListReducer.products;

  dispatch({ type: FETCH_PRODUCT_BY_CATEGORY_REQUEST });

  const productArray = Array.from(products.values());
  const filteredProducts = productArray.filter(p => p.categoriesIds.includes(categoryId));

  if (filteredProducts.length > 0) {
    dispatch({ type: FETCH_PRODUCT_BY_CATEGORY_SUCCESS, payload: filteredProducts });
    return;
  }

  try {
    const response = await getProducts();

    if (response.code === 1) {
      const categoryProducts = response.payload.filter(p => p.categoriesIds.includes(categoryId));
      dispatch({ type: FETCH_PRODUCT_BY_CATEGORY_SUCCESS, payload: categoryProducts });
    } else {
      dispatch({ type: FETCH_PRODUCT_BY_CATEGORY_FAILURE, payload: response.comment });
    }
  } catch (error: unknown) {
    dispatch({ type: FETCH_PRODUCT_BY_CATEGORY_FAILURE, payload: (error as Error).message || 'Une erreur inconnue est survenue.' });
  }
};

export const fetchProductListByKind = (kind: string) => async (dispatch: Dispatch<ProductActionTypes>, getState: () => RootState) => {
  const state = getState();
  const products = state.productListReducer.products;

  dispatch({ type: FETCH_PRODUCT_BY_CATEGORY_REQUEST });

  const productArray = Array.from(products.values());
  const filteredProducts = productArray.filter(p => p.kind === kind);

  if (filteredProducts.length > 0) {
    dispatch({ type: FETCH_PRODUCT_BY_CATEGORY_SUCCESS, payload: filteredProducts });
    return;
  }

  try {
    const response = await getProducts();

    if (response.code === 1) {
      const kindProducts = response.payload.filter(p => p.kind === kind);
      dispatch({ type: FETCH_PRODUCT_BY_CATEGORY_SUCCESS, payload: kindProducts });
    } else {
      dispatch({ type: FETCH_PRODUCT_BY_CATEGORY_FAILURE, payload: response.comment });
    }
  } catch (error: unknown) {
    dispatch({ type: FETCH_PRODUCT_BY_CATEGORY_FAILURE, payload: (error as Error).message || 'Une erreur inconnue est survenue.' });
  }
};

// retrieveProductById sert depuis le cache sans jamais le revalider : après une modification
// du produit (visibilité, prix), les listes continuaient d'afficher l'ancien `active`.
export const refreshProductById = async (productId: number): Promise<void> => {
  try {
    const response = await getProducts();
    if (response.code === 1) {
      const product = response.payload.find(p => p.id === productId);
      if (product) {
        store.dispatch({ type: FETCH_PRODUCT_BY_ID_SUCCESS, payload: product } as any);
      }
    }
  } catch { /* le cache garde l'ancienne valeur : la liste sera juste en retard */ }
};

export const retrieveProductById = async (productId: number): Promise<Product | null> => {
  const { dispatch, getState } = store;
  const state = getState();
  const products = state.productListReducer.products;

  if (products.has(productId)) {
    return products.get(productId) ?? null;
  }

  await dispatch(fetchProductById(productId));
  const updatedState = getState();
  return updatedState.productListReducer.products.get(productId) ?? null;
};

export const retrieveProductListByCategory = async (categoryId: number): Promise<Product[]> => {
  const { dispatch, getState } = store;
  const state = getState();
  const products = state.productListReducer.products;

  const productArray = Array.from(products.values());
  const filteredProducts = productArray.filter(p => p.categoriesIds.includes(categoryId));

  if (filteredProducts.length > 0) {
    return filteredProducts;
  }

  await dispatch(fetchProductListByCategory(categoryId));
  const updatedState = getState();
  return Array.from(updatedState.productListReducer.products.values()).filter(p => p.categoriesIds.includes(categoryId));
};

export const retrieveProductListByKind = async (kind: string): Promise<Product[]> => {
  const { dispatch, getState } = store;
  const state = getState();
  const products = state.productListReducer.products;

  const productArray = Array.from(products.values());
  const filteredProducts = productArray.filter(p => p.kind === kind);

  if (filteredProducts.length > 0) {
    return filteredProducts;
  }

  await dispatch(fetchProductListByKind(kind));
  const updatedState = getState();
  return Array.from(updatedState.productListReducer.products.values()).filter(p => p.kind === kind);
};

export const retrieveProductList = async (params: GetProductListParams = {}, filter: string = '', renew: boolean = false): Promise<Product[]> => {
  const { dispatch, getState } = store;
  const state = getState();
  const products = state.productListReducer.products;

  if (products.size > 0 && !renew) {
    let filteredProducts = Array.from(products.values());
    
    if (params.kind) {
      filteredProducts = filteredProducts.filter(p => p.kind === params.kind);
    }
    
    if (params.active !== undefined) {
      filteredProducts = filteredProducts.filter(p => p.active === params.active);
    }
    
    if (params.categoryId !== undefined) {
      const categoryId = params.categoryId;
      filteredProducts = filteredProducts.filter(p => p.categoriesIds.includes(categoryId));
    }
    
    return filteredProducts;
  }

  await dispatch(fetchProductList(params, filter, renew));
  const updatedState = getState();
  
  let result = Array.from(updatedState.productListReducer.products.values());
  
  if (params.kind) {
    result = result.filter(p => p.kind === params.kind);
  }
  
  if (params.active !== undefined) {
    result = result.filter(p => p.active === params.active);
  }
  
  if (params.categoryId !== undefined) {
    const categoryId = params.categoryId;
    result = result.filter(p => p.categoriesIds.includes(categoryId));
  }
  
  return result;
};